import { EmptyState } from '@today/ui-components'
import { Button, Textarea } from '@today/ui-primitives'
import { type ReactElement, useState } from 'react'
import type { NoteView, Translate } from './customer-types'

/** Props for {@link NotesTimelineView}; `onAdd` only when the viewer may write notes. */
export interface NotesTimelineViewProps {
	t: Translate
	notes: NoteView[]
	onAdd?: (body: string) => void
	onEdit?: (id: string, body: string) => void
	onDelete?: (id: string) => void
}

function NoteItem({ t, note, props }: { t: Translate; note: NoteView; props: NotesTimelineViewProps }) {
	const [editing, setEditing] = useState<string | null>(null)
	const own = note.isOwn && props.onEdit && props.onDelete
	return (
		<li className="rounded-ds-md border border-ds-border p-3 text-ds-sm">
			<p className="text-ds-text-muted">
				{`${note.author} · ${note.createdAt}`}
				{note.edited ? ` · ${t('notes.edited')}` : ''}
			</p>
			{editing === null ? (
				<p className="text-ds-text">{note.body}</p>
			) : (
				<Textarea aria-label={t('notes.placeholder')} value={editing} onChange={(e) => setEditing(e.target.value)} />
			)}
			{own ? (
				<div className="flex gap-2">
					{editing === null ? (
						<Button size="sm" variant="muted" onPress={() => setEditing(note.body)}>
							{t('actions.edit')}
						</Button>
					) : (
						<Button
							size="sm"
							onPress={() => {
								props.onEdit?.(note.id, editing)
								setEditing(null)
							}}
						>
							{t('actions.save')}
						</Button>
					)}
					<Button size="sm" variant="danger" onPress={() => props.onDelete?.(note.id)}>
						{t('actions.delete')}
					</Button>
				</div>
			) : null}
		</li>
	)
}

/** Notes timeline, newest first; edit/delete only on the viewer's own notes (organism). */
export function NotesTimelineView(props: NotesTimelineViewProps): ReactElement {
	const { t } = props
	const [draft, setDraft] = useState('')
	return (
		<section className="flex flex-col gap-3">
			{props.onAdd ? (
				<div className="flex flex-col gap-2">
					<Textarea
						aria-label={t('notes.placeholder')}
						placeholder={t('notes.placeholder')}
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
					/>
					<Button
						isDisabled={draft.trim() === ''}
						onPress={() => {
							props.onAdd?.(draft)
							setDraft('')
						}}
					>
						{t('actions.addNote')}
					</Button>
				</div>
			) : null}
			{props.notes.length === 0 ? (
				<EmptyState title={t('notes.empty')} />
			) : (
				<ul className="flex flex-col gap-2">
					{props.notes.map((note) => (
						<NoteItem key={note.id} t={t} note={note} props={props} />
					))}
				</ul>
			)}
		</section>
	)
}
